/**
 * Every user-facing string in the web UI, keyed by dotted path. `ru.ts` must
 * mirror these keys exactly — TypeScript enforces that.
 */
export const en = {
  // App chrome
  "nav.devices": "Devices",
  "nav.triggers": "Triggers",
  "nav.analyzes": "Analyze",
  "nav.settings": "Settings",
  "nav.agentApk": "Agent APK",
  "app.signOut": "Sign out",
  "lang.label": "Language",

  // Login
  "login.email": "Email",
  "login.password": "Password",
  "login.signIn": "Sign in",
  "login.signingIn": "Signing in…",
  "login.failed": "Sign in failed",
  "login.expired": "Your access ended on {date}. Contact us to extend it.",
  "login.expiredNoDate": "Your access has ended. Contact us to extend it.",
  "login.suspended": "This account is suspended. Contact us to restore access.",
  "login.consoleBlocked":
    "Access to this dashboard has been closed by the provider. Your phones are unaffected.",
  "login.tooManyAttempts": "Too many sign-in attempts. Try again in a few minutes.",
  // Only ever seen in a tab our own console opened, so it is written for us.
  "login.handoffExpired":
    "This support link has already been used or has expired. Open the customer's console again from the admin console.",

  // A console opened from ours: whose it is, and the way out of it
  "support.session": "Support session",
  "support.end": "End",

  // Account (the tenant this dashboard belongs to)
  "account.until": "Access until {date}",
  "account.unlimited": "No end date",
  "account.endsInDays": "Access ends in {n} days",
  "account.endsToday": "Access ends today",
  "account.ended": "Access ended on {date}",
  "account.suspended": "Account suspended",
  "account.plan": "Your plan",
  "account.devicesUsed": "Devices used",
  "account.deviceCap": "Device limit",
  "account.noCap": "No limit",

  // Agent APK
  "apk.title": "Agent APK",
  "apk.intro":
    "Generate an installer for your phones. Each build carries your own enrollment key, so a phone that installs it appears in Devices by itself.",
  "apk.generate": "Generate APK",
  "apk.generating": "Queueing…",
  "apk.label": "Batch label (optional)",
  "apk.labelHint": "Becomes a tag on every phone this build enrols.",
  "apk.key": "Enrollment key",
  "apk.newKey": "Create a new key",
  "apk.keyLegacy": "{name} — cannot be reused",
  "apk.keyUnavailable":
    "That key cannot be baked into an APK again \u2014 usually because it was made before keys could be reused. Pick another key, or leave the field on \u201cCreate a new key\u201d.",
  "apk.cap": "Device limit for this key (optional)",
  "apk.batchLabel": "Batch",
  "devices.noneDefined": "No analyze sets defined",
  "apk.builds": "Builds",
  "apk.created": "Created",
  "apk.version": "Agent",
  "apk.status": "Status",
  "apk.size": "Size",
  "apk.download": "Download",
  "apk.checksum": "SHA-256",
  "apk.noBuilds": "No builds yet.",
  "apk.noTemplate":
    "No agent release is available yet. We will publish one shortly — contact us if this persists.",
  "apk.keys": "Enrollment keys",
  "apk.keyName": "Name",
  "apk.keyDevices": "Phones enrolled",
  "apk.keyLastUsed": "Last used",
  "apk.revoke": "Revoke",
  "apk.revoked": "Revoked",
  "apk.noKeys": "No enrollment keys yet.",
  "apk.keyShownOnce": "Copy this key now — it is not shown again.",
  "apk.install": "Installing on a phone",
  "apk.install1": "Copy the APK to the phone and open it. Allow installing from this source.",
  "apk.install2": "Open QA Agent and grant Accessibility when it asks.",
  "apk.install3": "Turn off battery optimisation for QA Agent so it stays connected.",
  "apk.install4": "The phone appears under Devices within a minute.",
  "apk.design": "Screen design",
  "apk.builtInScreen": "Built-in screen",
  "apk.designLink": "Design a screen",
  "apk.designColumn": "Screen",
  "apk.designUnsupported":
    "The current agent release ({version}) cannot carry a custom screen yet. Contact us — 0.4.0 or newer is needed.",
  "apk.install2Designed": "Open QA Agent and tap \u201c{button}\u201d, then grant Accessibility.",
  "apk.modulesColumn": "Modules",
  "apk.designUses": "Design \u201c{design}\u201d uses: {uses}",
  "apk.usesModule": "{module} ({actions} actions, {values} values, {blocks} blocks)",
  "apk.modulesInBuild": "Modules in this build: {modules}.",
  "apk.modulesNone": "none",
  "apk.modulesLink": "Modules",
  "apk.moduleUnsupported":
    "The current agent release ({version}) predates the {module} module. It will be in your next build once we publish a newer release.",
  "apk.designModuleDisabled":
    "\u201c{design}\u201d uses {modules}, which is switched off, so the button would do nothing on the phone. Switch it on, or pick another screen:",
  "apk.copy": "Copy",
  "apk.copied": "Copied",

  // Building on this computer, in the desktop app (DESKTOP §7)
  "apk.buildHere": "Build on this computer",
  "apk.buildHereBusy": "Building…",
  "apk.buildOnServer": "Build on the server",
  "apk.buildHereHint":
    "Built here, the APK is signed with this computer\u2019s own key \u2014 so a phone running a server-built agent has to be uninstalled once before it can install this one. It enrols itself again straight after.",
  "apk.stageKey": "Creating the signing key\u2026",
  "apk.stageDownload": "Downloading the agent\u2026",
  "apk.stageStamp": "Stamping your settings into it\u2026",
  "apk.stageSign": "Signing\u2026",
  "apk.stageSave": "Saving\u2026",
  "apk.savedTo": "Saved to {path}",
  "apk.buildCancelled": "Cancelled before saving.",
  "apk.builtOnAComputer": "Built on a computer",
  "apk.signingKey": "Signing key",
  "apk.signingKeyIntro":
    "APKs built on this computer are signed with this key. Back it up: without it, a phone cannot upgrade in place to a later build and has to be reinstalled.",
  "apk.signingKeyNone": "Created on the first build on this computer.",
  "apk.signingKeyFingerprint": "Fingerprint",
  "apk.signingKeyCreated": "Created",
  "apk.keyExportPassword": "Password for the backup",
  "apk.exportKey": "Export\u2026",
  "apk.keyImportPassword": "Password of the backup",
  "apk.importKey": "Import\u2026",
  "apk.keyImportConfirm":
    "Replace the signing key on this computer? Phones running an APK signed with the current key will have to be reinstalled.",
  "apk.keyExported": "Backed up to {path}",
  "apk.keyImported": "Replaced. The new fingerprint is {fingerprint}",

  // Common
  "loading": "Loading {what}…",
  "loading.devices": "devices",
  "loading.triggers": "triggers",
  "loading.analyzes": "analyze sets",
  "loading.settings": "settings",
  "loading.device": "device",
  "time.secondsAgo": "{n}s ago",
  "time.minutesAgo": "{n}m ago",
  "time.hoursAgo": "{n}h ago",
  "time.daysAgo": "{n}d ago",

  // Statuses (device + the run/step kinds the CSS still styles)
  "status.online": "online",
  "status.busy": "busy",
  "status.needs_attention": "needs attention",
  "status.offline": "offline",
  "status.quarantined": "quarantined",
  "status.queued": "queued",
  "status.allocating": "allocating",
  "status.running": "running",
  "status.passed": "passed",
  "status.failed": "failed",
  "status.cancelled": "cancelled",
  "status.error": "error",
  "status.pending": "pending",
  "status.skipped": "skipped",
  "status.building": "building",
  "status.ready": "ready",
  "status.local": "on a computer",

  // Analyze kinds (shared by the Analyze page and the Devices checks)
  "kind.language": "Phone language",
  "kind.app": "App installed",
  "kind.country": "Country",
  "kindShort.language": "language",
  "kindShort.app": "app installed",
  "kindShort.country": "country",

  // Devices
  "devices.searchPlaceholder": "Search name, model, tag, language, country…",
  "devices.searchAria": "Search devices",
  "devices.clearSearch": "Clear search",
  "devices.filterByStatus": "Filter by status",
  "devices.all": "All",
  "devices.th.device": "Device",
  "devices.th.status": "Status",
  "devices.th.permissions": "Permissions",
  "devices.th.analyzes": "Analyzes",
  "devices.manageAnalyzes": "Manage analyze sets",
  "devices.th.locale": "Locale",
  "devices.th.added": "Added",
  "devices.notReportedYet": "No heartbeat has carried a permission report yet",
  "devices.notReported": "not reported yet",
  "devices.unnamed": "unnamed",
  "devices.noCriteria": "no criteria",
  "devices.enrolled": "Enrolled {at}",
  "devices.new": "new",
  "devices.lastHeartbeat": "Last heartbeat",
  "devices.expand": "Show details for {name}",
  "devices.collapse": "Hide details for {name}",
  "devices.openPanel": "Open this phone's panel",
  "devices.detail.phone": "Phone",
  "devices.detail.model": "Model",
  "devices.detail.os": "OS",
  "devices.detail.screen": "Screen",
  "devices.detail.battery": "Battery",
  "devices.detail.agent": "Agent",
  "devices.detail.sim": "SIM",
  "devices.detail.locale": "Locale",
  "devices.detail.tags": "Tags",
  "devices.detail.state": "State",
  "devices.detail.enrolled": "Enrolled",
  "devices.thisFilter": "this filter",
  "devices.noMatch": "No devices match {what}.",
  "devices.clearFilters": "Clear filters",
  "devices.empty": "No devices enrolled yet.",
  "devices.showing": "Showing {shown} of {total} devices",
  "devices.filtersOn": "filters are on",

  // Permission chips (Devices)
  "perm.granted": "Granted",
  "perm.notGranted": "Not granted",
  "perm.accessibility": "Accessibility",
  "perm.accessibility.why": "The control plane itself: gestures, text entry and tree capture.",
  "perm.screen_capture": "Screen capture",
  "perm.screen_capture.why":
    "The live picture and recordings. Android 11+ needs only Accessibility; Android 10 and older need a one-tap screen-capture consent on the phone.",
  "perm.ime": "Keyboard",
  "perm.ime.why": "The agent's own IME — password fields and key events need it.",
  "perm.notifications": "Notifications",
  "perm.notifications.why": "Hides the foreground-service notification; Android kills the agent sooner.",

  // Triggers
  "triggers.title": "Triggers",
  "triggers.new": "New trigger",
  "triggers.introA":
    "A trigger lets a phone record on its own: the agent watches for the condition and captures a window when it fires. Arm one on a phone from its",
  "triggers.devicePanelLink": "device panel",
  "triggers.th.name": "Name",
  "triggers.th.condition": "Condition",
  "triggers.th.recording": "Recording",
  "triggers.th.armedOn": "Armed on",
  "triggers.th.actions": "Actions",
  "triggers.condApp": "App opens",
  "triggers.condText": "Text appears",
  "triggers.notArmed": "Not armed",
  "triggers.edit": "Edit",
  "triggers.delete": "Delete",
  "triggers.deleteConfirm": "Delete trigger {name}?",
  "triggers.deleteArmedOne": "It is armed on one phone, which will stop watching.",
  "triggers.deleteArmedMany": "It is armed on {n} phones, which will stop watching.",
  "triggers.deleteGone": "This cannot be undone.",
  "triggers.deleteTrigger": "Delete trigger",
  "triggers.keepIt": "Keep it",
  "triggers.empty": "No triggers yet.",
  "triggers.createFirst": "Create your first trigger",
  "triggers.cancel": "Cancel",
  "triggers.chipAria": "{name}, {status}",
  "triggers.chipTitle": "{name} — {status}. Open the device panel.",
  "triggers.armedNoteOne": "Armed on one phone: saving pushes the new condition to it right away.",
  "triggers.armedNoteMany": "Armed on {n} phones: saving pushes the new condition to them right away.",
  "triggers.armedNoteNone": "Not armed on any phone yet.",
  "triggers.name": "Name",
  "triggers.nameHintEdit": "A trigger's name cannot be changed.",
  "triggers.nameHint": "Letters, digits, dots, dashes, underscores.",
  "triggers.description": "Description",
  "triggers.optional": "optional",
  "triggers.descriptionPlaceholder": "What this trigger is for",
  "triggers.firesWhen": "Fires when",
  "triggers.firesApp": "An app opens",
  "triggers.firesText": "Text appears on screen",
  "triggers.valueApp": "Package",
  "triggers.valueText": "Text to watch for",
  "triggers.valueAppHint": "Copy it from the Apps tab of a device panel.",
  "triggers.valueTextHint": "Matched anywhere on the screen, ignoring case.",
  "triggers.thenRecord": "Then record",
  "triggers.stopWindow": "For a fixed window",
  "triggers.stopOnExit": "While the app stays open",
  "triggers.recordFor": "Record for",
  "triggers.safetyCap": "Safety cap",
  "triggers.window": "Window",
  "triggers.durationHintCap": "Recording stops here even if the app never closes.",
  "triggers.durationHint": "{min} to {max} seconds.",
  "triggers.seconds": "seconds",
  "triggers.create": "Create trigger",
  "triggers.save": "Save changes",
  "triggers.created": "Created {name}.",
  "triggers.saved": "Saved {name}.",
  "triggers.duplicateA": "A trigger called",
  "triggers.duplicateB": "already exists. Pick another name.",
  "triggers.errNameEmpty": "Give the trigger a name.",
  "triggers.errNameLong": "Keep the name under {max} characters.",
  "triggers.errNameChars": "Letters, digits, dots, dashes and underscores only — no spaces.",
  "triggers.errPackageEmpty": "Which package should the phone watch for?",
  "triggers.errTextEmpty": "What text should the phone watch for?",
  "triggers.errDurationInt": "Whole seconds, please.",
  "triggers.errDurationRange": "Between {min} and {max} seconds.",
  "triggers.durationShort": "{n} s",
  "triggers.recOnExit": "While the app is open",
  "triggers.recOnExitCap": "While the app is open · up to {n} s",
  "triggers.subjectApp": "the app",
  "triggers.subjectText": "the text",
  "triggers.previewWhen": "When",
  "triggers.previewOpens": "opens, the phone records for",
  "triggers.previewOpensUntil": "opens, the phone records until it closes, stopping after",
  "triggers.previewAppears": "appears on screen, the phone records for",
  "triggers.previewMaxSuffix": " at most.",

  // Analyze
  "analyzes.title": "Analyze",
  "analyzes.setTitle": "Analyze sets",
  "analyzes.setIntro":
    "A named set of facts a phone must have — its language, an installed app, its country. A set can hold any number of conditions and only matches a phone when all of them do. Every phone in Devices shows a ✓ next to each enabled set it matches and ✗ when it does not; a disabled set is not judged at all.",
  "analyzes.namePlaceholder": "analyze name (e.g. us instagram phone)",
  "analyzes.removeCondition": "Remove this condition",
  "analyzes.addCondition": "+ condition",
  "analyzes.addConditionTitle": "Add another condition — a phone matches only when all of them hold",
  "analyzes.create": "Create",
  "analyzes.allConditionsNote": "A phone matches only when every condition holds.",
  "analyzes.th.name": "Name",
  "analyzes.th.checks": "Checks",
  "analyzes.th.matching": "Matching",
  "analyzes.th.created": "Created",
  "analyzes.matching": "{matching}/{total} devices",
  "analyzes.disabled": "disabled",
  "analyzes.toggleOffTitle": "Stop checking phones against this set",
  "analyzes.toggleOnTitle": "Start checking phones against this set",
  "analyzes.on": "on",
  "analyzes.off": "off",
  "analyzes.deleteConfirm": 'Delete analyze set "{name}"?',
  "analyzes.empty": "No analyze sets defined yet.",
  "analyzes.matchYes": "Matches",
  "analyzes.matchNo": "No match",

  // Settings
  "settings.title": "Settings",
  "settings.signedIn": "Signed in",
  "settings.role": "role: {role}",
  "settings.enrollmentKeys": "Enrollment keys",
  "settings.enrollmentKeysHint":
    "Bake a key into the agent APK and every phone that installs that build enrolls itself and appears under Devices — one build for the whole fleet. Revoking a key stops new phones; enrolled ones keep working.",
  "settings.enrollmentKeyBuild":
    "Build the APK with the key baked in, install it on every phone, and open the app once on each:",
  "settings.enrollmentKeyNamePlaceholder": "key name (e.g. fleet)",
  "settings.enrollmentKeyCapPlaceholder": "max phones (optional)",
  "settings.th.phones": "Phones",
  "settings.noEnrollmentKeys":
    "No keys yet — mint one to build an APK that enrolls every phone it is installed on.",
  "settings.apiKeys": "API keys",
  "settings.apiKeysHintA": "Used by the",
  "settings.apiKeysHintB": "CLI and CI. Set",
  "settings.apiKeysHintC": "to the value below.",
  "settings.mintedNote": "Copy this now — it is shown once and stored only as a hash.",
  "settings.keyNamePlaceholder": "key name (e.g. ci)",
  "settings.create": "Create",
  "settings.th.name": "Name",
  "settings.th.created": "Created",
  "settings.th.lastUsed": "Last used",
  "settings.revoked": "· revoked",
  "settings.keyLegacy": "· cannot be reused",
  "settings.never": "never",
  "settings.revoke": "Revoke",
  "settings.noKeys": "No keys yet.",
  "settings.secrets": "Secrets",
  "settings.secretsHintA": "Referenced as",
  "settings.secretsHintB":
    "Decrypted on the worker at run time and never returned here — the step log keeps the template, not the value.",
  "settings.valuePlaceholder": "value",
  "settings.save": "Save",
  "settings.delete": "Delete",
  "settings.noneStored": "None stored.",
  "settings.pools": "Pools",
  "settings.poolsHintA": "Runs target a pool rather than named phones:",
  "settings.noPools": "No pools defined.",
  "settings.channels": "Notification channels",
  "settings.channelsHint": "Credentials are write-only: they are stored, used, and never shown again.",
  "settings.channelNamePlaceholder": "channel name",
  "settings.add": "Add",
  "settings.delivered": "delivered ✓",
  "settings.failed": "failed: {error}",
  "settings.sendTest": "Send test",
  "settings.removeTitle": "Remove",
  "settings.subscribePlaceholder": "+ subscribe to an event…",
  "settings.noChannels": "No channels configured.",
  "settings.disabled": " · disabled",

  // Device panel
  "panel.back": "Back",
  "panel.home": "Home",
  "panel.recents": "Recents",
  "panel.enter": "Enter",
  "panel.notifs": "Notifs",
  "panel.quick": "Quick",
  "panel.volUp": "Vol +",
  "panel.volDown": "Vol −",
  "panel.swipeLeft": "Left",
  "panel.swipeRight": "Right",
  "panel.swipeUp": "Up",
  "panel.swipeDown": "Down",
  "panel.swipeTitle": "Swipe {dir}",
  "panel.hudTitle":
    "Delivered frame rate · round trip to the server · round trip of the last command through the phone · age of the picture on screen (approximate)",
  "panel.lockTitle": "Turn the screen off",
  "panel.unlockTitle": "Wake and dismiss the keyguard",
  "panel.hidePanel": "Hide the side panel",
  "panel.showPanel": "Show the side panel",
  "panel.rename": "Rename device",
  "panel.save": "Save",
  "panel.cancel": "Cancel",
  "panel.foreground": "Foreground app",
  "panel.streaming": "streaming",
  "panel.stalled": "stalled",
  "panel.reconnecting": "reconnecting…",
  "panel.notConnected": "not connected",
  "panel.deviceRefused": "The device refused the command",
  "panel.screenAlt": "device screen",
  "panel.waitingFrame": "waiting for the first frame…",
  "panel.noFrameYet":
    "Connected, but the phone has not sent a picture yet. Its journal on the Devices page says what it is doing.",
  "panel.captureNeedsConsent":
    "Android {version} has no accessibility screenshots. Screen capture needs a one-tap consent on the phone.",
  "panel.captureStopped": "Screen capture was stopped on the phone. Request it again.",
  "panel.requestCapture": "Request screen capture on the phone",
  "panel.captureConsentHint":
    "Then tap “Start now” on the phone. The picture appears here within a few seconds.",
  "panel.captureNeedsAgentUpdate":
    "This phone runs an older agent build that cannot capture the screen on Android {version}. Install the current agent APK on it, then request screen capture here.",
  "panel.captureAccessibilityOff":
    "Accessibility is off on the phone. Enable the QA Agent service under Settings → Accessibility; the picture appears once it is on.",
  "panel.captureScreenOff": "The phone's screen is off.",
  "panel.wake": "Wake the screen",
  "panel.capturePlatformError":
    "The phone keeps failing to capture: {message}. The projection backend may work where the built-in screenshot does not.",
  "panel.requestCaptureFallback": "Switch to screen-capture consent",
  "panel.noStream": "no stream",
  "panel.deviceOffline": "Device is not connected",
  "panel.recChip": "Trigger armed on this phone",
  "panel.typePlaceholder": "Type into the focused field…",
  "panel.send": "Send",
  "panel.gestureHint": "Drag to swipe · scroll to fling · double-click to double-tap",
  "panel.tabTrigger": "Trigger",
  "panel.tabApps": "Apps",
  "panel.tabModules": "Modules",
  "panel.tabNote": "Note",
  "panel.noModules": "This phone's build carries no modules.",
  "panel.moduleState.ok": "working",
  "panel.moduleState.needs_permission": "needs permission",
  "panel.moduleState.failed": "failed",
  "panel.moduleRun": "Run",
  "panel.moduleRunning": "Running\u2026",
  "panel.moduleClose": "Close",
  "panel.moduleNotOnDevice":
    "This phone's build does not carry {module}. Switch it on and install a new build.",
  "panel.moduleOffline": "The phone is not connected right now.",
  "panel.moduleFailed": "The module refused: {message}",
  "panel.triggerHeading": "Trigger scenario",
  "panel.triggerIntro": "The phone records by itself when the condition fires, then tells the server.",
  "panel.disarm": "Disarm",
  "panel.conditionUnavailable": "condition: (details unavailable)",
  "panel.durationWindow": " · {n}s window",
  "panel.condWhenPackage": "when {package} opens",
  "panel.condWhenText": 'when "{text}" appears',
  "panel.noTriggerArmed": "No trigger armed on this phone.",
  "panel.chooseTrigger": "Choose a trigger…",
  "panel.arm": "Arm",
  "panel.noTriggersYet": "No trigger scenarios yet — create one on the Triggers page.",
  "panel.latestCapture": "Latest capture",
  "panel.loadingShort": "loading…",
  "panel.loading": "Loading…",
  "panel.framesToMp4": "{n} frames → mp4",
  "panel.noVideoYet": "No video yet — it appears when a capture finishes.",
  "panel.appsHeading": "Apps",
  "panel.refresh": "Refresh",
  "panel.appsIntro": "Launcher apps on this phone. Click one to open it.",
  "panel.filterApps": "Filter by name or package…",
  "panel.connectToLoad": "Connect to the device to load its apps.",
  "panel.noAppsFilter": "No apps match that filter.",
  "panel.noApps": "The phone reported no launcher apps.",
  "panel.noteHeading": "Device note",
  "panel.editNote": "Edit the device note",
  "panel.noNote": "No note yet — lab quirks, SIM details, who borrowed it.",
  "panel.notePlaceholder": "e.g. API 34 test SIM, flaky Wi-Fi, borrowed by the payments team…",

  // --- Admin console (a separate bundle, the same dictionary) --------------
  "admin.title": "Automation LTD Admin",
  "admin.supportsDesigns": "Supports screen designs (spec v1)",
  "admin.designsChip": "designs v{v}",
  "admin.rolloutWithDesign": "{n} of them carry the customer's own screen.",
  "admin.nav.tenants": "Customers",
  "admin.nav.releases": "Agent releases",
  "admin.nav.admins": "Admins",
  "admin.nav.audit": "Audit",
  "admin.signIn": "Sign in to the admin console",
  "admin.tenants": "Customers",
  "admin.newTenant": "New customer",
  "admin.search": "Search by name or slug",
  "admin.name": "Name",
  "admin.slug": "Slug",
  "admin.role": "Role",
  "admin.status.active": "active",
  "admin.status.suspended": "suspended",
  "admin.userActive": "active",
  "admin.userDisabled": "disabled",
  "admin.status": "Status",
  "admin.expires": "Access",
  "admin.expiryLeft": "{n} days left",
  "admin.expiryOneDay": "1 day left",
  "admin.expiryToday": "Ends today",
  "admin.expiryEnded": "Ended {date}",
  "admin.devices": "Devices",
  "admin.users": "Logins",
  "admin.lastSeen": "Last seen",
  "admin.storage": "Storage",
  "admin.noTenants": "No customers yet. Create the first one.",
  "admin.create": "Create customer",
  "admin.creating": "Creating…",
  "admin.cancel": "Cancel",
  "admin.close": "Close",
  "admin.loginEmail": "Login email",
  "admin.loginEmailHint": "Left blank, we generate one from the slug.",
  "admin.contactEmail": "Contact email",
  "admin.devicesOnlineOfTotal": "{online} of {total} devices online",
  "admin.batchLabel": "Batch",
  "admin.deviceCap": "Device limit",
  "admin.notes": "Notes",
  "admin.accessUntil": "Access until",
  "admin.preset.14d": "14 days",
  "admin.preset.30d": "30 days",
  "admin.preset.90d": "90 days",
  "admin.preset.1y": "1 year",
  "admin.preset.none": "No end date",
  "admin.credentials": "Credentials",
  "admin.credentialsOnce": "Shown once. Copy them now and send them to the customer.",
  "admin.password": "Password",
  "admin.email": "Email",
  "admin.back": "Back to customers",
  "admin.extend": "Extend",
  "admin.suspend": "Suspend",
  "admin.resume": "Resume",
  "admin.consoleAccess": "Console access",
  "admin.consoleAccessHelp":
    "Blocks the dashboard, the API and API keys for this customer. Phones stay enrolled and connected, so restoring brings everything back with nothing to reinstall.",
  "admin.consoleBlocked": "Console blocked",
  "admin.consoleBlockedSince": "Blocked since {date}",
  "admin.consoleNoReason": "No reason given",
  "admin.consoleBlock": "Block console access",
  "admin.consoleBlockConfirm": "Block access",
  "admin.consoleRestore": "Restore console access",
  "admin.consoleReason": "Reason (optional)",
  "admin.consoleReasonHint": "Shown to the customer when they try to sign in",
  "admin.consoleAlreadyBlocked": "Someone else has already closed this console.",
  "admin.consoleNotBlocked": "This console is already open.",
  "admin.consoleColumn": "Console",
  "admin.open": "Open",
  "admin.openConsole": "Open console",
  "admin.openingConsole": "Opening…",
  "admin.openConsoleHelp":
    "Opens this customer's dashboard in a new tab — signed in as one of their own logins, with no password. It is recorded in their activity below.",
  "admin.openConsoleSuspended": "Resume this account before opening its console.",
  "admin.openConsoleExpired": "Extend this customer's access before opening their console.",
  "admin.openConsoleClosed": "Restore this customer's console access before opening it.",
  "admin.openConsoleNoLogin": "This customer has no active login to open the console as.",
  "admin.openConsoleDisabledLogin": "That login is disabled. Activate it, or open the console as another one.",
  "admin.openConsoleNotOpened":
    "The console did not open. Allow pop-ups for this address — or update the desktop app, if that is where you are — and try again.",
  "admin.disconnect": "Force phones to redial",
  "admin.resetPassword": "Reset password",
  "admin.deactivate": "Deactivate",
  "admin.activate": "Activate",
  "admin.addLogin": "Add a login",
  "admin.keys": "Enrollment keys",
  "admin.builds": "Agent builds",
  "admin.registry": "Devices",
  "admin.auditTail": "Recent activity",
  "admin.dangerZone": "Danger zone",
  "admin.purge": "Purge this customer",
  "admin.purgeWarning": "Deletes every row and every stored file for this customer. There is no undo.",
  "admin.purgeConfirm": "Type {slug} to confirm",
  "admin.rollout": "Build for every customer",
  "admin.rolloutQueued": "Queued {n} builds of agent {version}. Each customer will find it on their Agent APK page in a few seconds.",
  "admin.rolloutSkipped": "Skipped (suspended or lapsed): {slugs}",
  "admin.rolloutDesignSkipped":
    "{n} of them went out without the customer's screen: it uses a module this release does not carry, or one they switched off.",
  "admin.modules": "Modules",
  "admin.designsAffected":
    "Agent {version} is current now. These customers' designs name a module it does not carry — their next build ships without that screen:",
  "admin.releases": "Agent releases",
  "admin.version": "Version",
  "admin.current": "Current",
  "admin.makeCurrent": "Make current",
  "admin.upload": "Upload a release",
  "admin.uploading": "Uploading…",
  "admin.uploadHint": "The unsigned release APK from `make agent-template`.",
  "admin.mapping": "R8 mapping (mapping.txt)",
  "admin.mappingHint":
    "Keeps crash traces from this release readable. Gradle writes it next to the APK.",
  "admin.mappingColumn": "Mapping",
  "admin.mappingMissing": "no mapping",
  "admin.addMapping": "Add mapping",
  "admin.noMappingStored":
    "Agent {version} was uploaded without its mapping.txt, so its crash traces cannot be read back. Only the machine that built it still has the file.",
  "admin.noReleases": "No agent release uploaded yet. Customers cannot generate an APK until one is.",
  "admin.admins": "Platform admins",
  "admin.addAdmin": "Add an admin",
  "admin.lastLogin": "Last sign-in",
  "admin.actor": "Actor",
  "admin.action": "Action",
  "admin.when": "When",
  "admin.noAudit": "Nothing recorded yet.",
  "admin.never": "never",

  // --- screen design constructor (DESIGN §9.6) -----------------------------
  "nav.design": "App design",
  "nav.modules": "Modules",
  "design.title": "App design",
  "design.intro":
    "Design the screen the QA Agent opens on. Pick one when you generate an APK.",
  "design.new": "New design",
  "design.create": "Create",
  "design.creating": "Creating…",
  "design.cancel": "Cancel",
  "design.name": "Name",
  "design.empty": "No designs yet.",
  "design.emptyHint":
    "Until you choose one for a build, the phone shows the built-in screen: one button on black.",
  "design.default": "Default",
  "design.open": "Open",
  "design.setDefault": "Set as default",
  "design.duplicate": "Duplicate",
  "design.delete": "Delete",
  "design.confirmDelete": "Delete for good",
  "design.updated": "Updated {when}",
  "design.back": "Back to designs",
  "design.save": "Save",
  "design.saving": "Saving…",
  "design.undo": "Undo",
  "design.redo": "Redo",
  "design.buildApk": "Build APK",
  "design.more": "More",
  "design.stateSaved": "Saved",
  "design.stateUnsaved": "Unsaved changes",
  "design.stateIssues": "Fix {n} before saving",
  "design.blocks": "Blocks",
  "design.addBlock": "Add block",
  "design.paletteHint": "Click to add, or drag onto the phone.",
  "design.noBlocks": "No blocks yet — add one above, or drop an image on the phone.",
  "design.emptyFrame": "Drop an image here, or add a block",
  "design.dragHint": "Drag to reorder — or select the row and use ↑ and ↓",
  "design.zoom": "Zoom",
  "design.zoomFit": "Fit",
  "design.group.content": "Content",
  "design.group.style": "Style",
  "design.group.size": "Size",
  "design.group.background": "Background",
  "design.group.layout": "Layout",
  "design.stackHint":
    "Blocks stack top to bottom. Drag to reorder; use alignment, Full width and Spacer blocks to place them; the Screen panel sets where the stack sits.",
  "design.remove": "Remove",
  "design.theme": "Screen",
  "design.screenHint": "Background and spacing",
  "design.previewNote":
    "Preview is approximate: fonts and wrapping differ slightly by phone, and text follows the phone's font size.",
  "design.issues": "Fix before saving",
  "design.warnings": "Worth knowing",
  "design.moduleDisabledBanner":
    "This screen uses {modules}, which is switched off. It stays saved, it does nothing on the phone, and a build with it is refused.",
  "design.openModules": "Open Modules",
  "design.restorePrompt": "You have unsaved changes to this design from earlier.",
  "design.restore": "Restore unsaved changes",
  "design.discardDraft": "Discard them",
  "design.staleMessage": "This design was saved elsewhere. Reload to see it; your draft is kept.",
  "design.reload": "Reload",
  "design.nameIconNote":
    "The app's name and launcher icon are not part of a design yet — every build installs as QaAgent.",
  "design.block.text": "Text",
  "design.block.button": "Button",
  "design.block.image": "Image",
  "design.block.spacer": "Spacer",
  "design.block.divider": "Divider",
  "design.block.status": "Status panel",
  "design.blockHint.text": "A line or a paragraph.",
  "design.blockHint.button": "Does one thing from a fixed list.",
  "design.blockHint.image": "A logo or a picture — drop one on the phone.",
  "design.blockHint.spacer": "Empty room between blocks.",
  "design.blockHint.divider": "A thin rule across the screen.",
  "design.blockHint.status": "Live rows: is this phone enrolled and working?",
  "design.text": "Text",
  "design.label": "Label",
  "design.size": "Size",
  "design.weight": "Weight",
  "design.weight.regular": "Regular",
  "design.weight.medium": "Medium",
  "design.weight.bold": "Bold",
  "design.font": "Font",
  "design.font.sans": "Sans",
  "design.font.serif": "Serif",
  "design.font.mono": "Monospace",
  "design.color": "Colour",
  "design.mutedColor": "Label colour",
  "design.textColor": "Text colour",
  "design.fill": "Fill",
  "design.stroke": "Border",
  "design.align": "Align",
  "design.align.left": "Left",
  "design.align.center": "Centre",
  "design.align.right": "Right",
  "design.radius": "Corner radius",
  "design.width": "Width",
  "design.height": "Height",
  "design.thickness": "Thickness",
  "design.inset": "Inset",
  "design.fullWidth": "Full width",
  "design.buttonSize": "Height",
  "design.buttonSize.sm": "Small",
  "design.buttonSize.md": "Medium",
  "design.buttonSize.lg": "Large",
  "design.variant": "Style",
  "design.variant.filled": "Filled",
  "design.variant.outlined": "Outlined",
  "design.variant.text": "Text only",
  "design.action": "What it does",
  "design.actionGroup.core": "App",
  "design.actionGroup.off": "Unavailable",
  "design.moduleAsks": "\ud83d\udd12 Asks for {permission} on the first tap.",
  "design.moduleActionUnknown":
    "This button runs an action of {module}, which this release does not describe.",
  "design.moduleBlockUnknown":
    "This screen shows a block of {module}, which this release does not describe.",
  "design.moduleBlockFlex":
    "Fills the screen; other blocks keep their place above or below it.",
  "design.paramRequired": "required",
  "design.action.enable_accessibility": "Enable accessibility",
  "design.action.ignore_battery_optimization": "Keep running in background",
  "design.action.allow_screen_capture": "Allow screen capture",
  "design.action.open_url": "Open a link",
  "design.action.open_app_settings": "Open app settings",
  "design.action.copy_device_id": "Copy device id",
  "design.action.forget_enrollment": "Leave this account",
  "design.actionHint.enable_accessibility":
    "Opens the system screen where the QA service is switched on. Every design needs one of these.",
  "design.actionHint.ignore_battery_optimization":
    "Asks Android to stop putting the agent to sleep — the difference between a phone that stays connected and one that drops off.",
  "design.actionHint.allow_screen_capture":
    "Asks for screen-capture consent. Needed on Android 9 and 10, and on some phones after that.",
  "design.actionHint.open_url": "Opens an https link in the phone's browser.",
  "design.actionHint.open_app_settings":
    "Opens this app's page in Settings — where Android 13+ hides the restricted-setting unlock.",
  "design.actionHint.copy_device_id":
    "Copies this phone's device id to the clipboard. The first thing support asks for.",
  "design.actionHint.forget_enrollment":
    "Asks first, then removes this phone from your account. It re-enrols on the next start.",
  "design.url": "Link",
  "design.alt": "Description (for screen readers)",
  "design.spacer.flex": "Take the remaining space",
  "design.spacer.hint": "A flexible spacer pushes what follows it to the bottom of the screen.",
  "design.status.hint": "Real values from the phone. This is how someone holding it can see it is working.",
  "design.rows": "Rows",
  "design.row.enrollment": "Enrollment",
  "design.row.accessibility": "QA service",
  "design.row.capture": "Screen capture",
  "design.row.version": "Agent version",
  "design.row.tenant": "Account",
  "design.background": "Background",
  "design.background.solid": "One colour",
  "design.background.gradient": "Gradient",
  "design.background.image": "Image",
  "design.gradientStop": "Colour {n}",
  "design.addStop": "Add colour",
  "design.removeStop": "Remove colour",
  "design.angle": "Direction",
  "design.scale": "Fit",
  "design.scale.cover": "Fill the screen",
  "design.scale.contain": "Fit inside",
  "design.overlay": "Darken the image",
  "design.overlayColor": "Overlay",
  "design.statusBarIcons": "Status bar icons",
  "design.statusBarIcons.light": "Light",
  "design.statusBarIcons.dark": "Dark",
  "design.padding": "Screen padding",
  "design.gap": "Gap between blocks",
  "design.verticalAlign": "Vertical position",
  "design.verticalAlign.top": "Top",
  "design.verticalAlign.center": "Centre",
  "design.verticalAlign.bottom": "Bottom",
  "design.noImage": "None",
  "design.uploadImage": "Upload image",
  "design.uploading": "Uploading…",
  "design.uploadHint": "PNG, JPEG or WebP. {used} of {max} used; drop a file here.",
  "design.assetsFull": "All {max} image slots are used.",
  "design.removeImage": "Remove image",
  "design.dropImage": "Drop to add an image",
  "design.notAnImage": "{name} is not a PNG, JPEG or WebP image.",
  "design.imageTooLarge": "{name} is larger than {mb} MB.",
  "design.blocksFull": "This design already holds all {max} blocks.",

  // --- modules (MODULES §8.7) ---------------------------------------------
  "modules.title": "Modules",
  "modules.intro":
    "Extras the current agent release can build into your app. Switching one on changes what your next APK carries.",
  "modules.emptyRelease": "This release carries no modules.",
  "modules.on": "On",
  "modules.off": "Off",
  "modules.toggleAria": "Switch {module} on or off",
  "modules.adds": "Adds {actions} actions, {values} values, {blocks} blocks, {commands} commands",
  "modules.actions": "Actions",
  "modules.values": "Values",
  "modules.blocks": "Blocks",
  "modules.commands": "Commands",
  "modules.permissionOptional": "optional",
  "modules.permissionOptionalHint":
    "The module works without it — the phone asks only when a page needs it.",
  "modules.size": "about {kb} KB",
  "modules.needsAgent": "Needs agent {version} or newer, which is not the current release.",
  "modules.usedBy": "Used by:",
  "modules.usedByWhileOff":
    "while it is off, a build with one of these screens is refused.",
  "modules.requires": "Switch {module} on first — this module needs it.",
  "modules.apkNote": "Phones already installed need the new APK.",

  // --- Console chrome and the sign-in screen ------------------------------
  "app.name": "Automation LTD",
  "app.menu": "Open menu",
  "app.closeMenu": "Close menu",
  "nav.group.fleet": "Fleet",
  "nav.group.rollout": "Rollout",
  "nav.group.account": "Account",

  "login.title": "Sign in",
  "login.subtitle": "Use the email and password we sent you.",
  "login.emailPlaceholder": "you@company.com",

  "admin.brandSub": "Platform admin",
  "admin.nav.group.customers": "Customers",
  "admin.nav.group.platform": "Platform",

  // --- The desktop app (DESKTOP §2.4) --------------------------------------
  "loading.desktop": "the desktop app",
  "desktop.versionLine": "Version {version} · released {date}",
  "desktop.versionOnly": "Version {version}",
  "desktop.downloadMacArm": "Download for Mac (Apple silicon)",
  "desktop.downloadMacIntel": "Download for Mac (Intel)",
  "desktop.downloadWindows": "Download for Windows",
  "desktop.fileVersion": "version {version}",
  "desktop.whichMac":
    "Not sure which Mac you have? The Apple menu → About This Mac says which chip is inside.",
  "desktop.noInstallers": "This release carries nothing to download yet.",

  "settings.desktopApp": "Desktop app",
  "settings.desktopHint":
    "The dashboard in a window on your own computer. It builds the agent APK here instead of on our servers — which is what “Build on this computer” on the Agent APK page needs — and keeps itself up to date.",
  "settings.desktopNone": "No desktop release has been published yet.",
  "settings.desktopRunning": "You are running the desktop app, version {version}.",
  "settings.desktopUpdate":
    "Version {version} is available — the app offers it under Server → Check for Updates…",

  "admin.desktop": "Desktop apps",
  "admin.nav.desktop": "Desktop apps",
  "admin.desktopAdminApp": "Automation LTD Admin",
  "admin.desktopAdminAppHint":
    "This console in a window of its own. It carries no APK bridge — it builds nothing.",
  "admin.desktopCustomerApp": "Automation LTD (customer app)",
  "admin.desktopCustomerAppHint":
    "What a customer installs: their own dashboard, plus the local APK build.",
  "admin.desktopCustomerFinds":
    "Customers download it themselves, from Settings → Desktop app on their dashboard.",
  "admin.desktopNotPublished": "Not published yet.",
  "admin.desktopPublishA": "Publish a new version with",
  "admin.desktopPublishB": "after bumping the version in",
  "admin.desktopPublishC":
    "— installed apps offer the update on their next check.",
// --- Ready builds: the library of finished APKs -----------------------------
"nav.readyApks": "Ready-to-use APKs",
"readyBuilds.title": "Ready-to-use APKs",
"readyBuilds.intro":
  "Finished, signed builds anyone with console access can install as-is. Drop an .apk below — it goes to the library for everyone; pin it to one login if it is not ready for the team yet.",
"readyBuilds.dropHere": "Drag an .apk here, or",
"readyBuilds.pickFile": "pick a file",
"readyBuilds.notApk": "\"{name}\" is not an .apk file.",
"readyBuilds.notesPlaceholder": "Notes for this build (what it is, what changed…)",
"readyBuilds.visibleHint": "Uploaded builds are visible to everyone who can open this console.",
"readyBuilds.upload": "Add to library",
"readyBuilds.cancel": "Cancel",
"readyBuilds.save": "Save",
"readyBuilds.empty": "No builds in the library yet — drop the first one above.",
"readyBuilds.colBuild": "Build",
"readyBuilds.colNotes": "Notes",
"readyBuilds.colVisible": "Visible to",
"readyBuilds.colAdded": "Added",
"readyBuilds.editNotes": "Edit",
"readyBuilds.visibleAll": "Everyone",
"readyBuilds.visibleOne": "Only {email} — open to all?",
"readyBuilds.download": "Download",
"readyBuilds.copyLink": "Copy link",
"readyBuilds.copied": "Copied",
"readyBuilds.linkHint":
  "Anyone with this link can download the APK — no sign-in needed.",
"readyBuilds.delete": "Delete",
"readyBuilds.confirmDelete": "Delete for good?",
};
